import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { StatusBar, Style } from '@capacitor/status-bar';
import { isNativePlatform } from '@/lib/platform';
import { error } from '@/utils/logger';

/**
 * Routes whose top region renders a dark/photographic hero behind a transparent
 * header. The status bar overlays them, so it needs white text/icons (Style.Light).
 *
 * Anything not listed here defaults to dark text on a light header background
 * (Style.Dark). Add new routes here when they ship hero-style headers.
 */
const LIGHT_STATUS_BAR_PREFIXES = [
    '/creators',
    '/advisors',
    '/destinations',
    '/travel-styles',
    '/trips',
    '/itinerary/',
    '/list/',
    '/place/',
    '/post/',
    '/profile/',
    '/storefront/',
    '/book/',
    '/recommendation/',
    '/s/',
    '/onboarding',
];

function statusBarStyleForRoute(pathname: string): Style {
    if (pathname === '/') return Style.Light;
    if (LIGHT_STATUS_BAR_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
        return Style.Light;
    }
    return Style.Dark;
}

/**
 * Configures the native status bar on iOS/Android via Capacitor and keeps the
 * style in sync with the current route — dark hero pages get white icons,
 * white-backgrounded pages get dark icons. WebView always overlays the status
 * bar so content can bleed under the dynamic island / notch.
 *
 * No-op on web. Call once near the app root (e.g. RootLayout).
 */
export function useNativeStatusBar(): void {
    const { pathname } = useLocation();

    useEffect(() => {
        if (!isNativePlatform()) return;
        StatusBar.setOverlaysWebView({ overlay: true }).catch((e) => {
            error('[useNativeStatusBar] Failed to set overlaysWebView', e);
        });
    }, []);

    useEffect(() => {
        if (!isNativePlatform()) return;
        const style = statusBarStyleForRoute(pathname);
        StatusBar.setStyle({ style }).catch((e) => {
            error('[useNativeStatusBar] Failed to set status bar style', e);
        });
    }, [pathname]);
}
